import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import type { TextFieldProps } from "@mui/material/TextField";
import { MRT_GlobalFilterTextField, type MRT_RowData, type MRT_TableInstance } from "material-react-table";
import NumberBadge from "_shared/_views/badges/number-badge";
import { ListHeadingWidth, useMeasuredWidth } from "_shared/_views/list-pages/list-heading-width";
import type { ReactNode, SyntheticEvent } from "react";

/**
 * What the narrow toolbar needs beside the tabs: the search field at its default width with its icon
 * (235px measured in Chromium), the gap, and the ⋯ button (40px), with a few pixels to spare. Any
 * less and the search would be squeezed below the width it has on a row of its own.
 */
export const NARROW_TOOLBAR_MIN_WIDTH = 288;

// #region Styled Components
/** The wide layout's search, table tools and create control, stacked at the right of the title. */
export const ListPageToolbarButtons = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
/** The narrow layout's search and table tools; `ListPageHeading` decides where the row goes. */
export const ListPageMobileToolbar = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  justifyContent: "space-between",
  minWidth: 0,
  width: "100%",
  // The search takes whatever the icons leave (#6716). MRT draws it inside a horizontal Collapse, so
  // the Collapse is the row's child, and each of its wrappers has to stretch as well.
  "& > .MuiCollapse-root": {
    flex: "1 1 auto",
    minWidth: 0,
  },
  "& > .MuiCollapse-root .MuiCollapse-wrapper, & > .MuiCollapse-root .MuiCollapse-wrapperInner": {
    width: "100%",
  },
  "& .MuiTextField-root": {
    flex: "1 1 auto",
    minWidth: 0,
    width: "100%",
  },
}));
/**
 * The narrow layout's search box, for `ListPageMobileToolbar`. Its placeholder is just "Search": on a
 * phone "Search archived work agreements" was cut to "Search archived work…" (#6716). The page's own
 * words stay the box's accessible name, so a screen reader still hears what it searches.
 */
export function ListPageMobileSearch<TData extends MRT_RowData>(props: Readonly<{ table: MRT_TableInstance<TData> }>) {
  // The table is one object MRT edits in place, so its options are read afresh on every render.
  "use no memo";

  const { table } = props;
  const { localization, muiSearchTextFieldProps } = table.options;
  const pageProps: TextFieldProps =
    (typeof muiSearchTextFieldProps === "function"
      ? muiSearchTextFieldProps({ table })
      : muiSearchTextFieldProps) ?? {};
  const name = typeof pageProps.placeholder === "string" ? pageProps.placeholder : localization.search;
  const slotProps = (pageProps.slotProps ?? {}) as Record<string, object | undefined>;
  return (
    <MRT_GlobalFilterTextField
      table={table}
      placeholder={localization.search}
      slotProps={{ ...slotProps, htmlInput: { ...slotProps.htmlInput, "aria-label": name } }}
    />
  );
}

const HeadingContainer = styled("div")(() => ({
  display: "flex",
  flex: "1 1 0",
  flexDirection: "column",
  maxWidth: "100%",
  minWidth: 0,
  width: "100%",
}));
// On a narrow screen the chips sit beside the tabs when both fit, and wrap under them when not: a row
// each spent a phone's height on a strip of two tabs and one chip (#6716).
const TabsAndChipsRow = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    alignItems: "stretch",
  },
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  // Neither the tabs nor the chips shrink, so when both do not fit beside the search and create
  // controls the chips move under the tabs rather than running beneath the create control.
  flexWrap: "wrap",
  gap: theme.spacing(1),
  minWidth: 0,
  width: "100%",
}));
const TitleRow = styled("div")(({ theme }) => ({
  alignItems: "center",
  columnGap: theme.spacing(1),
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  justifyContent: "space-between",
  width: "100%",
}));
const PageTabs = styled(Tabs)(() => ({
  flexShrink: 0,
  minWidth: 0,
  maxWidth: "100%",
  "& .MuiTabs-scroller": {
    minWidth: 0,
  },
}));
const Title = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
/**
 * The narrow layout's search and table tools, in the tabs' row. Below `md` the tabs leave most of
 * their row empty, so the toolbar takes what is left of it. It asks for the room it has on a row of
 * its own; when the tabs leave less than that, the row wraps and the toolbar takes a row of its own.
 * The heading's own width decides, not the window's, so no breakpoint picks between the two. On a
 * phone it always takes its own row, after the chips, as it did before (#7221).
 */
const ToolbarSlot = styled("div", {
  shouldForwardProp: (prop) => prop !== "minToolbarWidth",
})<{ minToolbarWidth: number }>(({ theme, minToolbarWidth }) => ({
  display: "flex",
  flex: `1 1 ${minToolbarWidth}px`,
  minWidth: 0,
  [theme.breakpoints.down("sm")]: {
    flexBasis: "100%",
    order: 1,
  },
}));
// The chip groups, then any control of the tab's own. They wrap within the width they are given
// rather than standing a column of rows, so a long run of chips never runs past it. On a wide screen
// they keep their width beside the tabs while they fit, move under them when they do not, and are never
// wider than the row: Documents' six categories, two owners and Archived ran off a 1280px pane (#6720).
const ChipsSlot = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexShrink: 0,
  flexWrap: "wrap",
  gap: theme.spacing(1),
  maxWidth: "100%",
  minWidth: 0,
  [theme.breakpoints.down("md")]: {
    flexShrink: 1,
  },
}));
// #endregion

/**
 * The top of a list page: its title, its tabs, the toolbar on a narrow screen, its chips, and the
 * criteria its URL sets (#6712).
 *
 * Every list page draws the same heading, so a reader finds each control in the same place on each
 * page. A page renders it in its table's `renderTopToolbarCustomActions` and fills the slots it has;
 * the wide layout's search and table tools go in `renderToolbarInternalActions`, beside it.
 */
export function ListPageHeading(
  props: Readonly<{
    title: string;
    /** The narrow layout's create control, at the end of the title row. */
    titleAction?: ReactNode;
    /** The page's tabs (`ListPageTabs`): one per kind of thing the page lists. */
    tabs?: ReactNode;
    /** The tab's chips (`ListChips`), and any control of the tab's own after them. */
    chips?: ReactNode;
    /**
     * The narrow layout's search and table tools (`ListPageMobileToolbar`): beside the tabs when they
     * fit, on a row of their own when they do not, and always on a row of their own on a phone.
     */
    mobileToolbar?: ReactNode;
    /**
     * The room `mobileToolbar` needs beside the tabs, for a toolbar that holds more than the search
     * and the ⋯ button, such as the Views button (#6717). `NARROW_TOOLBAR_MIN_WIDTH` when left out.
     */
    mobileToolbarMinWidth?: number;
    /** The criteria the URL sets, as removable chips, on the last row (#6221). */
    criteria?: ReactNode;
  }>,
) {
  const { title, titleAction, tabs, chips, mobileToolbar, criteria } = props;
  const minToolbarWidth = props.mobileToolbarMinWidth ?? NARROW_TOOLBAR_MIN_WIDTH;
  // A narrow page (one that hands in its narrow toolbar) with no tabs and no create control has room
  // beside its title, so its chips go there rather than on a row of their own (#6716). They wrap under
  // the title when they do not fit.
  const chipsBesideTitle = Boolean(mobileToolbar) && !tabs && !titleAction;
  // The filters read how wide the heading is, which says whether they fit beside the tabs (#6720).
  const [headingRef, headingWidth] = useMeasuredWidth();
  return (
    <HeadingContainer ref={headingRef}>
      <ListHeadingWidth value={headingWidth}>
        <TitleRow>
          <Title variant="h4">{title}</Title>
          {chipsBesideTitle ? <ChipsSlot>{chips}</ChipsSlot> : titleAction}
        </TitleRow>
        {(!chipsBesideTitle || mobileToolbar) && (
          <TabsAndChipsRow>
            {tabs}
            {mobileToolbar && <ToolbarSlot minToolbarWidth={minToolbarWidth}>{mobileToolbar}</ToolbarSlot>}
            {!chipsBesideTitle && <ChipsSlot>{chips}</ChipsSlot>}
          </TabsAndChipsRow>
        )}
        {criteria}
      </ListHeadingWidth>
    </HeadingContainer>
  );
}

/** One tab of a list page, and how many rows it holds. */
export type ListPageTab<TTab extends string> = Readonly<{
  value: TTab;
  label: string;
  /** What the count is a count of, for its tooltip: "open proposal(s)". */
  counts: string;
  /** The tab's count, as its query answers it; undefined while the query is on the wire. */
  count: number | undefined;
  isLoading: boolean;
  /** The badge's color while it counts something; grey otherwise. */
  badgeColor?: "info";
}>;

/**
 * A list page's tabs, each with how many rows it holds. A tab is a different kind of thing; which of
 * them the list shows is the chips' job. A tab's count is its own, and does not move with the chips.
 *
 * The strip scrolls at every width, with arrows only when it overflows: it shares its row with the
 * chips, the search and the create control, so the room it gets is not the window's.
 */
export function ListPageTabs<TTab extends string>(
  props: Readonly<{
    value: TTab;
    tabs: readonly ListPageTab<TTab>[];
    onChange: (event: SyntheticEvent, tab: TTab) => void;
  }>,
) {
  const { value, tabs, onChange } = props;
  return (
    <PageTabs
      value={value}
      indicatorColor="primary"
      textColor="primary"
      variant="scrollable"
      scrollButtons="auto"
      allowScrollButtonsMobile
      onChange={onChange}
    >
      {tabs.map((tab) => (
        <Tab
          key={tab.value}
          value={tab.value}
          label={
            <NumberBadge
              isLoading={tab.isLoading}
              tooltip={`${tab.count ?? 0} ${tab.counts}`}
              badgeCount={tab.count ?? 0}
              color={tab.badgeColor}
              text={tab.label}
            />
          }
        />
      ))}
    </PageTabs>
  );
}
